import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../api/api";
import LoadingLogo from "../components/Loading";
import { routes } from "../config/utility";
import { faCheckCircle, faPaw } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import routes, StatusBadge, etc. as needed

const SingleUser = () => {
  const { email } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate(); // For redirecting

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/users/${email}`);
      setUserData(response.data);
    } catch (error) {
      // Failed to fetch user data
      navigate("/not-found");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingLogo />;
  }

  if (!userData) {
    navigate("/not-found");
    return null; // Avoid rendering anything if we're redirecting
  }

  // Destructure from your JSON shape
  const {
    id,
    name,
    username,
    email: userEmail,
    roles = [],
    phone,
    join_at,
    dob,
    profile_photo,
    pin_location,
    pets,
    walksRequested,
    deliveriesRequested,
    walkerFeedbacks,
    driverFeedbacks,
    walksAssigned,
    deliveriesAssigned,
    paid,
    membership,
  } = userData;

  // Helper booleans
  const isUser = roles.includes("user");
  const isWalker = roles.includes("walker");
  const isDriver = roles.includes("driver");

  // Example status badge component placeholder
  // to style the statuses
  function StatusBadge({ status }) {
    const statusColors = {
      finding_driver: "bg-yellow-100 text-yellow-800",
      driver_assigned: "bg-blue-100 text-blue-800",
      heading_to_pickup: "bg-orange-100 text-orange-800",
      arrived_at_pickup: "bg-orange-200 text-orange-900",
      en_route_to_dropoff: "bg-purple-100 text-purple-800",
      arrived_at_dropoff: "bg-purple-200 text-purple-800",
      en_route_to_return: "bg-pink-100 text-pink-800",
      arrived_at_return: "bg-pink-200 text-pink-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      requested: "bg-gray-100 text-gray-800",
      finding_walker: "bg-yellow-50 text-yellow-700",
      walker_assigned: "bg-blue-50 text-blue-700",
      in_progress: "bg-blue-200 text-blue-900",
      picked_up: "bg-orange-50 text-orange-700",
      // Add more as needed
    };

    const badgeClasses = statusColors[status] || "bg-gray-100 text-gray-800";
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeClasses}`}>
        {status?.replace(/_/g, " ").toUpperCase()}
      </span>
    );
  }

  return (
    <div className="p-2 sm:p-8">
      {/* Heading */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-tblue text-3xl font-bold">User Detail</h1>
      </div>

      {/* Main Container: glassy, translucent */}
      <div className="mx-auto shadow-xl rounded-xl overflow-hidden border border-white/10 bg-white/20 backdrop-blur-md ring-1 ring-white/10 transition-all hover:shadow-2xl">

        {/* Header Section */}
        <div className="bg-gradient-to-r from-tblue to-primary p-6 flex flex-col md:flex-row items-center md:items-start gap-4 relative">
          {/* Floating translucent card behind the user image for style */}
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm -z-10 rounded-xl" />

          {/* Profile Photo */}
          <img
            src={profile_photo || "https://via.placeholder.com/150"}
            alt={name || "User"}
            className="w-32 h-32 rounded-full border-4 border-white object-cover shadow-lg"
          />

          {/* User Info */}
          <div className="text-white space-y-2">
            <h2 className="text-2xl font-bold">{name || "Unnamed User"}

              {isUser && (
                <>
                  {paid ? (
                    <span
                      key={"Paid"}
                      className="inline-block bg-green-500 text-white text-xs font-semibold  px-2 mx-2 py-1 rounded-full"
                    >
                      Paid User
                    </span>
                  ) : (
                    <span
                      key={"Unpaid"}
                      className="inline-block bg-red-500 text-white text-xs font-semibold mx-2 px-2 py-1 rounded-full"
                    >
                      Free User
                    </span>
                  )}
                </>
              )}
            </h2>
            <p className="text-sm">
              <strong>Username:</strong> {username || "N/A"}
            </p>
            <p className="text-sm">
              <strong>Email:</strong> {userEmail || "No email"}
            </p>
            <p className="text-sm">
              <strong>Phone:</strong> {phone || "No phone"}
            </p>
            <p className="text-sm">
              <strong>Joined:</strong> {join_at || "Unknown date"}
            </p>
            {dob && (
              <p className="text-sm">
                <strong>DOB:</strong> {dob}
              </p>
            )}
            {/* Roles Badges */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {roles.map((r) => (
                <span
                  key={r}
                  className="inline-block bg-secondary text-black text-xs font-semibold px-2 py-1 rounded-full"
                >
                  {r.toUpperCase()}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Pin Location */}
        {pin_location && (
          <div className="px-6 py-4 border-b border-white/20 bg-white/30 backdrop-blur-sm text-gray-800">
            <p className="text-sm">
              <strong>Pin Location:</strong> {pin_location.address || "N/A"}
              <span className="ml-2 text-gray-500 text-xs">
                (Lat: {pin_location.latitude}, Lng: {pin_location.longitude})
              </span>
            </p>
          </div>
        )}

        {/* Body Content */}
        <div className="p-6 space-y-8 text-bodyMain">
          {/* 1. If user (client) */}
          {isUser && (
            <section className="space-y-6">
              <h3 className="text-xl font-semibold text-tblue">
                Client / User Info
              </h3>

              {/* Membership Details */}
              {paid && membership && (
                <div>
                  <h4 className="text-lg font-bold mb-3 text-gray-800 flex items-center gap-2">
                    <FontAwesomeIcon icon={faPaw} />
                    <span>Membership Details</span>
                  </h4>

                  {/* Card with subtle gradient background */}
                  <div className="bg-gradient-to-tr from-pink-50 to-rose-100 border border-rose-200 rounded-lg p-4 shadow-md relative overflow-hidden">
                    {/* Optional decorative paw icon in background */}
                    <div className="absolute opacity-10 -top-10 -right-10">
                      <FontAwesomeIcon icon={faPaw} size="6x" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 relative z-10">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Membership</p>
                        <p className="text-base font-semibold text-gray-900">{membership.name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Price</p>
                        <p className="text-base text-gray-900">{membership.price} AED</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Status</p>
                        <p className="text-base text-gray-900">{membership.status}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Start Date</p>
                        <p className="text-base text-gray-900">{membership.start_date}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">End Date</p>
                        <p className="text-base text-gray-900">{membership.end_date}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Auto Renew</p>
                        <p className="text-base text-gray-900">
                          {membership.auto_renew ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>

                    {/* Membership Permissions */}
                    <div className="mt-5 relative z-10">
                      <h5 className="text-sm font-bold text-gray-800 mb-2">
                        Membership Permissions
                      </h5>
                      <ul className="space-y-1">
                        {Object.entries(membership.permissions).map(([key, value]) => (
                          <li
                            key={key}
                            className="flex items-center text-sm text-gray-700"
                          >
                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                            <span>
                              <strong>{key.replace(/_/g, ' ')}:</strong> {value}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Permissions */}
              {userData.permission && (
                <div className="mt-8">
                  <h4 className="text-lg font-bold mb-2 text-gray-800 flex items-center gap-2">
                    <FontAwesomeIcon icon={faPaw} />
                    <span>Current User's Permissions</span>
                  </h4>

                  <div className="bg-gradient-to-tr from-blue-50 to-indigo-100 border border-indigo-200 rounded-lg p-4 shadow-md relative overflow-hidden">
                    <div className="absolute opacity-10 -top-10 -right-10">
                      <FontAwesomeIcon icon={faPaw} size="6x" />
                    </div>

                    <ul className="space-y-1 relative z-10">
                      {Object.entries(userData.permission).map(([key, value]) => (
                        <li
                          key={key}
                          className="flex items-center text-sm text-gray-700"
                        >
                          <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                          <span>
                            <strong>{key.replace(/_/g, ' ')}:</strong> {value}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Pets */}
              {Array.isArray(pets) && pets.length > 0 ? (
                <div>
                  <h4 className="text-md font-bold mb-2 text-body">
                    Pets
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {pets.map((pet) => (
                      <div
                        key={pet.id}
                        className="bg-white/60 backdrop-blur-sm border border-white/30 rounded-lg p-3 flex flex-col items-center hover:shadow-md transition transform hover:-translate-y-1"
                      >
                        <img
                          src={
                            pet.primary_image_url ||
                            "https://via.placeholder.com/80"
                          }
                          alt={pet.name}
                          className="w-16 h-16 object-cover rounded-full shadow"
                        />
                        <p className="mt-2 text-sm text-gray-800 font-semibold">
                          {pet.name}
                        </p>
                        <p className="text-xs text-gray-600">
                          {pet.type?.name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-700">No pets found.</p>
              )}

              {/* Walks Requested */}
              {Array.isArray(walksRequested) && walksRequested.length > 0 && (
                <div>
                  <h4 className="text-md font-bold mb-2 text-body">
                    Walks Requested
                  </h4>
                  <div className="space-y-2">
                    {walksRequested.map((walk) => (
                      <Link
                        key={walk.id}
                        to={routes.walk_single.replace(':walk_id?', walk.id)}
                        className="block"
                      >
                        <div
                          className="border border-gray-300 bg-white/60 backdrop-blur-sm rounded-lg p-3 flex items-center justify-between hover:shadow-md transition hover:-translate-y-1"
                        >
                          <span className="text-sm font-semibold text-gray-800">
                            Walk #{walk.id} – Total: {walk.total} AED
                          </span>
                          <StatusBadge status={walk.status} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Deliveries Requested */}
              {Array.isArray(deliveriesRequested) && deliveriesRequested.length > 0 && (
                <div>
                  <h4 className="text-md font-bold mb-2 text-body">
                    Deliveries Requested
                  </h4>
                  <div className="space-y-2">
                    {deliveriesRequested.map((delivery) => (
                      <Link
                        key={delivery.id}
                        to={routes.delivery_single.replace(':delivery_id?', delivery.id)}
                        className="block"
                      >
                        <div
                          className="border border-gray-300 bg-white/60 backdrop-blur-sm rounded-lg p-3 flex items-center justify-between hover:shadow-md transition hover:-translate-y-1"
                        >
                          <span className="text-sm font-semibold text-gray-800">
                            Delivery #{delivery.id} –{" "}
                            {delivery.two_way ? "Two Way" : "One Way"} – Total:{" "}
                            {delivery.total_charge} AED
                          </span>
                          <StatusBadge status={delivery.status} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* 2. If walker */}
          {isWalker && (
            <section className="space-y-6">
              <h3 className="text-xl font-semibold text-tblue">
                Walker Dashboard
              </h3>

              {/* Walks Assigned */}
              {Array.isArray(walksAssigned) && walksAssigned.length > 0 ? (
                <div>
                  <h4 className="text-md font-bold mb-2 text-body">
                    Assigned Walks
                  </h4>
                  <div className="space-y-2">
                    {walksAssigned.map((walk) => (
                      <Link
                        key={walk.id}
                        to={routes.walk_single.replace(':walk_id?', walk.id)}
                        className="block"
                      >
                        <div
                          className="border border-gray-300 bg-white/60 backdrop-blur-sm rounded-lg p-3 flex items-center justify-between hover:shadow-md transition hover:-translate-y-1"
                        >
                          <span className="text-sm font-semibold text-gray-800">
                            Walk #{walk.id} – {walk.total} AED
                          </span>
                          <StatusBadge status={walk.status} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-700">No assigned walks.</p>
              )}

              {/* Walker Feedbacks */}
              {Array.isArray(walkerFeedbacks) && walkerFeedbacks.length > 0 ? (
                <div>
                  <h4 className="text-md font-bold mb-2 text-body">Feedback</h4>
                  <div className="space-y-2">
                    {walkerFeedbacks.map((feedback) => (
                      <Link
                        key={feedback.id}
                        to={routes.walk_single.replace(':walk_id?', feedback.service_id)}
                        className="block"
                      >
                        <div
                          className="bg-white/60 backdrop-blur-sm p-3 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center hover:shadow-md transition hover:-translate-y-1"
                        >
                          <div>
                            <p className="font-semibold text-gray-800">
                              Rating: {feedback.rating}
                            </p>
                            <p className="text-sm text-gray-600">
                              {feedback.feedback}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Service Type: {feedback.service_type}
                            </p>
                          </div>
                          {feedback.user && (
                            <div className="mt-2 sm:mt-0 sm:ml-4 text-xs text-gray-500">
                              from: {feedback.user.name} (
                              {feedback.user.email})
                            </div>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-700">No walker feedback found.</p>
              )}
            </section>
          )}

          {/* 3. If driver */}
          {isDriver && (
            <section className="space-y-6">
              <h3 className="text-xl font-semibold text-tblue">
                Driver Dashboard
              </h3>

              {/* Deliveries Assigned */}
              {Array.isArray(deliveriesAssigned) && deliveriesAssigned.length > 0 ? (
                <div>
                  <h4 className="text-md font-bold mb-2 text-body">
                    Assigned Deliveries
                  </h4>
                  <div className="space-y-2">
                    {deliveriesAssigned.map((delivery) => (
                      <Link
                        key={delivery.id}
                        to={routes.delivery_single.replace(':delivery_id?', delivery.id)}
                        className="block"
                      >
                        <div
                          className="border border-gray-300 bg-white/60 backdrop-blur-sm rounded-lg p-3 flex items-center justify-between hover:shadow-md transition hover:-translate-y-1"
                        >
                          <span className="text-sm font-semibold text-gray-800">
                            Delivery #{delivery.id} –{" "}
                            {delivery.two_way ? "Two Way" : "One Way"} –{" "}
                            {delivery.total_charge} AED
                          </span>
                          <StatusBadge status={delivery.status} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-700">No assigned deliveries.</p>
              )}

              {/* Driver Feedbacks */}
              {Array.isArray(driverFeedbacks) && driverFeedbacks.length > 0 ? (
                <div>
                  <h4 className="text-md font-bold mb-2 text-body">Feedback</h4>
                  <div className="space-y-2">
                    {driverFeedbacks.map((feedback) => (
                      <Link
                        key={feedback.id}
                        to={routes.delivery_single.replace(':delivery_id?', feedback.service_id)}
                        className="block"
                      >
                        <div
                          className="bg-white/60 backdrop-blur-sm p-3 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center hover:shadow-md transition hover:-translate-y-1"
                        >
                          <div>
                            <p className="font-semibold text-gray-800">
                              Rating: {feedback.rating}
                            </p>
                            <p className="text-sm text-gray-600">
                              {feedback.feedback}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Service Type: {feedback.service_type}
                            </p>
                          </div>
                          {feedback.user && (
                            <div className="mt-2 sm:mt-0 sm:ml-4 text-xs text-gray-500">
                              from: {feedback.user.name} ({feedback.user.email})
                            </div>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-700">No driver feedback found.</p>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleUser;
